import React, { useState, useEffect } from 'react';
import Header from './Header';
import Footer from './Footer';
import { Calendar } from 'lucide-react';

const ScheduleMeeting = () => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availableDates, setAvailableDates] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);

  // Generate all possible time slots from 9:30 AM to 6:00 PM (30-minute intervals)
  const generateAllTimeSlots = () => {
    const slots = [];
    for (let hour = 9; hour <= 18; hour++) {
      const periods = hour === 9 ? [30] : [0, 30];
      for (const minute of periods) {
        if (hour === 18 && minute === 30) continue; // Skip 6:30 PM
        
        const formattedHour = hour > 12 ? hour - 12 : hour;
        const amPm = hour >= 12 ? 'PM' : 'AM';
        const formattedMinute = minute === 0 ? '00' : minute;
        slots.push({
          value: `${hour}:${formattedMinute}`,
          label: `${formattedHour}:${formattedMinute} ${amPm}`
        });
      }
    }
    return slots;
  };
  
  const allTimeSlots = generateAllTimeSlots();

  // Filter time slots based on current time (for today only)
  useEffect(() => {
    if (!selectedDate) {
      setAvailableTimeSlots([]);
      return;
    }
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const selectedDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
    
    // If selected date is today, filter time slots that are at least 2 hours ahead
    if (selectedDay.getTime() === today.getTime()) {
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();
      
      // Filter slots that are at least 2 hours ahead of current time
      const filtered = allTimeSlots.filter(slot => {
        const [slotHour, slotMinute] = slot.value.split(':').map(Number);
        
        // Convert current time and slot time to minutes for easy comparison
        const currentTimeInMinutes = currentHour * 60 + currentMinute;
        const slotTimeInMinutes = slotHour * 60 + slotMinute;
        
        // Check if slot is at least 2 hours (120 minutes) ahead
        return slotTimeInMinutes - currentTimeInMinutes >= 60;
      });
      
      setAvailableTimeSlots(filtered);
    } else {
      // For future dates, show all time slots
      setAvailableTimeSlots(allTimeSlots);
    }
  }, [selectedDate]);

  // Generate calendar days
  useEffect(() => {
    // For demo purposes, let's assume all weekdays in current month are available
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    const dates = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      // Skip weekends (Saturday and Sunday)
      if (date.getDay() !== 0 && date.getDay() !== 6) {
        dates.push(date);
      }
    }
    
    setAvailableDates(dates);
  }, [currentMonth]);

  const handleDateClick = (date) => {
    setSelectedDate(date);
    setSelectedSlot(null); // Reset selected slot when date changes
  };

  const handleSlotClick = (slot) => {
    setSelectedSlot(slot);
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDate || !selectedSlot) {
      alert("Please select a date and time slot");
      return;
    }

    setIsSubmitting(true);
    try {
      const SENDY_SUBSCRIBE_URL = "https://maxiomwealth.com/app/api/newsendy/";
      const contact_us_information = ['stock-advisory'];
      // Compose a detailed message
      const meetingDetails = `Meeting Request:\nName: ${name}\nEmail: ${email}\nPhone: ${phone}\nDate: ${selectedDate ? selectedDate.toLocaleDateString() : ''}\nTime: ${selectedSlot}\nMessage: ${message}`;
      const customer_message = name ? `${name} wants to schedule a meeting with advisor.\n${meetingDetails}` : meetingDetails;

      // Step 1: Initialize session
      console.log("[DEBUG] Initializing session...");
      const sessionResponse = await fetch(SENDY_SUBSCRIBE_URL, {
        method: 'GET',
        credentials: 'include'
      });
      const cookies = sessionResponse.headers.get('set-cookie') || '';
      console.log("[DEBUG] Session cookies:", cookies);

      // Step 2: Prepare payload
      const params = new URLSearchParams();
      params.append('email', email);
      params.append('mobile_number', phone);
      params.append('start_my_switch', 'false');
      params.append('name', name);
      contact_us_information.forEach(item => params.append('contact_us_information[]', item));
      params.append('message', message ? meetingDetails : customer_message);
      // Add schedule info to 'from' field
      const scheduleInfo = `Scheduled Meeting: Date - ${selectedDate ? selectedDate.toLocaleDateString() : ''}, Time - ${selectedSlot}`;
      params.append('from', scheduleInfo);
      params.append('channel', 'web');
      params.append('campaign', scheduleInfo);
      params.append('source', 'https://maxiomwealth.com/meeting');

      console.log("[DEBUG] Complete request payload:", params.toString());

      // Step 3: Submit data
      const response = await fetch(SENDY_SUBSCRIBE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Cookie': cookies,
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json'
        },
        credentials: 'include',
        body: params
      });

      console.log("[DEBUG] Response status:", response.status);
      const responseData = await response.json();
      console.log("[DEBUG] API response:", responseData);

      setIsSubmitting(false);
      setSubmitStatus('success');
      // Reset form after a delay
      setTimeout(() => {
        setName('');
        setEmail('');
        setPhone('');
        setMessage('');
        setSelectedDate(null);
        setSelectedSlot(null);
        setSubmitStatus(null);
      }, 5000);
    } catch (error) {
      setIsSubmitting(false);
      alert("There was an error submitting your request. Please try again.");
      console.error("[ERROR] Meeting API submission failed:", error);
    }
  };

  // Format date for display
  const formatDate = (date) => {
    if (!date) return '';
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  // Calendar month name and year
  const monthName = currentMonth.toLocaleString('default', { month: 'long' });
  const year = currentMonth.getFullYear();

  // Generate calendar days display
  const renderCalendar = () => {
    const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();
    const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
    const days = [];

    // Fill in empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<div key={`empty-${i}`} className="h-10 w-10"></div>);
    }

    // Fill in the days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      const isWeekend = date.getDay() === 0 || date.getDay() === 6;
      const isAvailable = !isWeekend;
      const isToday = new Date().toDateString() === date.toDateString();
      const isSelected = selectedDate && selectedDate.toDateString() === date.toDateString();
      const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));

      days.push(
        <div 
          key={day} 
          className={`flex items-center justify-center h-10 w-10 rounded-full cursor-pointer mx-auto ${
            isSelected ? 'bg-primary-500 text-white' : 
            isToday ? 'border border-primary-300' : 
            isPast ? 'text-gray-300' : 
            isAvailable ? 'hover:bg-primary-100' : 'text-gray-300'
          } ${!isAvailable || isPast ? 'cursor-not-allowed' : ''}`}
          onClick={() => isAvailable && !isPast && handleDateClick(date)}
        >
          {day}
        </div>
      );
    }

    return days;
  };

  // Message to display when no slots are available for today
  const noSlotsMessage = () => {
    if (selectedDate && availableTimeSlots.length === 0) {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const selectedDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
      
      if (selectedDay.getTime() === today.getTime()) {
        return (
          <div className="p-4 bg-[#F49611]/10 rounded-md text-center">
            <p className="text-[#113262] font-medium">No more available slots for today. Please select a future date.</p>
          </div>
        );
      }
    }
    return null;
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-grow">
        {/* Hero Section with Background Shapes */}
        <div className="bg-gradient-to-r from-[#113262] to-[#1C52A0] text-white py-12 md:py-20 relative overflow-hidden">
          {/* Content */}
          <div className="max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-10 lg:py-12 relative z-10">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 tracking-tight text-center">Book Your Consultation</h1>
            <div className="w-24 h-1 bg-[#F49611] mx-auto mb-6"></div>
            <p className="max-w-2xl mx-auto text-lg text-white/90 text-center">
              Please provide your details below to book a personalized wealth management consultation with one of our financial advisors.
            </p>
          </div>
        </div>
          
        <div className="max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
          {submitStatus === 'success' ? (
            <div className="bg-white shadow-lg rounded-lg p-8 text-center max-w-2xl mx-auto">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-primary-500 mb-4">Consultation Scheduled!</h2>
              <p className="text-gray-700 mb-4">Thank you for scheduling a consultation with MaxiomWealth. We look forward to discussing your financial goals and helping you achieve them.</p>
              <p className="text-gray-600 mb-6">You will receive a confirmation email shortly with all the details of your appointment.</p>
              <div className="p-4 bg-primary-50 rounded-md mb-4 inline-block">
                <p className="text-primary-800 font-medium">A member of our team will contact you within 24 hours to confirm your appointment.</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 bg-white rounded-lg shadow-md overflow-hidden">
              {/* Calendar Section */}
              <div className="p-4 sm:p-6 lg:p-8 bg-[#113262] text-white">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-white">Select Date</h2>
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-5 w-5 text-[#F49611]" />
                  </div>
                </div>
                
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <button 
                      className="flex items-center justify-center w-8 h-8 rounded-full bg-[#F49611]/20 text-white hover:bg-[#F49611]/30"
                      onClick={handlePrevMonth}
                    >
                      &larr;
                    </button>
                    <h3 className="text-lg font-medium text-white">{monthName} {year}</h3>
                    <button 
                      className="flex items-center justify-center w-8 h-8 rounded-full bg-[#F49611]/20 text-white hover:bg-[#F49611]/30"
                      onClick={handleNextMonth}
                    >
                      &rarr;
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-7 gap-1 text-center mb-2">
                    {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
                      <div key={index} className="text-sm font-medium text-white/70">
                        {day}
                      </div>
                    ))}
                  </div>
                  
                  <div className="grid grid-cols-7 gap-1">
                    {renderCalendar()}
                  </div>
                </div>
                
                {selectedDate && (
                  <div>
                    <h3 className="text-lg font-medium mb-3 text-[#F49611]">Available Time Slots for {formatDate(selectedDate)}</h3>
                    {noSlotsMessage()}
                    {availableTimeSlots.length > 0 && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {availableTimeSlots.map((slot) => (
                          <button
                            key={slot.value}
                            className={`py-2 px-1 sm:px-3 text-xs sm:text-sm rounded-md border transition-all duration-200 ${
                              selectedSlot === slot.value 
                                ? 'bg-[#F49611] text-white border-[#F49611] shadow-md' 
                                : 'border-[#1C52A0]/30 hover:border-[#F49611] hover:bg-[#F49611]/20 text-white'
                            }`}
                            onClick={() => handleSlotClick(slot.value)}
                          >
                            {slot.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Form Section */}
              <div className="p-4 sm:p-6 lg:p-8">
                <h2 className="text-xl font-semibold text-[#113262] mb-6">Your Information</h2>
                
                <form onSubmit={handleSubmit}>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                      <input
                        type="text"
                        id="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C52A0] focus:border-transparent transition-all bg-gray-50"
                        placeholder="Your name"
                        required
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                      <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C52A0] focus:border-transparent transition-all bg-gray-50"
                        placeholder="Your email"
                        required
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                      <input
                        type="tel"
                        id="phone"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C52A0] focus:border-transparent transition-all bg-gray-50"
                        placeholder="Your contact number"
                        required
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">Additional Information</label>
                      <textarea
                        id="message"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        rows={4}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C52A0] focus:border-transparent transition-all bg-gray-50"
                        placeholder="Please share any specific topics you'd like to discuss during our consultation."
                      ></textarea>
                    </div>
                    
                    <div className="pt-4">
                      <button
                        type="submit"
                        className={`w-full bg-gradient-to-r from-[#113262] to-[#1C52A0] text-white py-3 px-6 rounded-lg transition-all hover:opacity-95 ${
                          isSubmitting || !selectedDate || !selectedSlot ? 'opacity-60 cursor-not-allowed' : ''
                        }`}
                        disabled={isSubmitting || !selectedDate || !selectedSlot}
                      >
                        Book Your Consultation
                      </button>
                    </div>
                    
                    {selectedDate && selectedSlot && (
                      <div className="mt-4 p-4 bg-[#E8EEF6] border border-[#1C52A0]/20 rounded-md">
                        <p className="text-[#113262]">
                          <span className="font-medium">Selected Date & Time:</span> {formatDate(selectedDate)} at {
                            allTimeSlots.find(slot => slot.value === selectedSlot)?.label
                          }
                        </p>
                      </div>
                    )}
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ScheduleMeeting;